import {
  type AgentPolicy,
  type AgentPolicyUpdate,
  type AiActivityEntry,
  type AiDraft,
  type ConversationState,
  type CustomerRecord,
  type EscalationLadder,
  type EscalationReasonCode,
  type KnowledgeSource,
} from '@unipostudio/shared-types';
import { api } from './api';
import { mockInboxAgent } from './mock/inbox-agent.mock';

/**
 * Sample drafts stand in for the agent only on a developer's machine that
 * asked for them (`VITE_INBOX_SAMPLE_DRAFTS=true` in a dev build). Anywhere
 * else a failed draft is an error the draft box explains: a sample there
 * once looked like the agent's answer and could be sent to a customer.
 */
const SAMPLE_DRAFTS = import.meta.env.DEV && String(import.meta.env['VITE_INBOX_SAMPLE_DRAFTS']) === 'true';

/** A draft came back with no text: shown as the generic "could not draft" message. */
export class EmptyDraftError extends Error {
  constructor() {
    super('empty draft');
    this.name = 'EmptyDraftError';
  }
}

/** The state as the API returns it: when it last changed, for reopening a resolved conversation on a new message. */
export type StoredConversationState = ConversationState & { updatedAt?: string };

/**
 * Everything the agentic inbox needs beyond plain events.
 *
 * All of this used to be served by a mock adapter in the browser, so a policy
 * a person saved, a conversation a colleague had taken over and a note written
 * about a customer all stayed on one machine. It now talks to the API.
 *
 * The one thing still served locally is a sample draft, and only on a
 * developer's machine that asked for it (SAMPLE_DRAFTS); it is always
 * labelled as a sample and can never be sent.
 */

/** Conversation keys contain a colon, so they have to be encoded into the path. */
function conversationPath(key: string, suffix = ''): string {
  return `/inbox/agent/conversations/${encodeURIComponent(key)}${suffix}`;
}

/** A knowledge source as listed, with why it cannot be used when it failed (`file_not_read`, or the reader's reason). */
export type KnowledgeSourceView = KnowledgeSource & { error?: string | null };

export interface EscalateInput {
  levelId: string;
  reason: EscalationReasonCode;
  note: string | null;
}

export interface SaveLadderResult {
  ladder: EscalationLadder;
  /** How many conversations were moved off a level that no longer exists. */
  movedConversations: number;
}

export const inboxAgentService = {
  async getPolicy(): Promise<AgentPolicy> {
    const res = await api.get<AgentPolicy>('/inbox/agent/policy');
    return res.data;
  },

  /** Consents go up as `true`/`false`; the server records who gave them and when. */
  async savePolicy(policy: AgentPolicyUpdate): Promise<AgentPolicy> {
    const res = await api.put<AgentPolicy>('/inbox/agent/policy', policy);
    return res.data;
  },

  async getLadder(): Promise<EscalationLadder> {
    const res = await api.get<EscalationLadder>('/inbox/agent/ladder');
    return res.data;
  },

  /**
   * Replaces the ladder. Removing a level needs somewhere for its
   * conversations to go, or the server refuses the change.
   */
  async saveLadder(
    ladder: EscalationLadder,
    reassignTo?: { fromLevelId: string; toLevelId: string },
  ): Promise<SaveLadderResult> {
    const res = await api.put<SaveLadderResult>('/inbox/agent/ladder', {
      levels: ladder.levels,
      ...(reassignTo ? { reassignTo } : {}),
    });
    return res.data;
  },

  async getStates(keys: readonly string[] = []): Promise<Record<string, StoredConversationState>> {
    const res = await api.get<Record<string, StoredConversationState>>(
      '/inbox/agent/conversations/state',
      keys.length > 0 ? { params: { keys: keys.map((k) => encodeURIComponent(k)).join(',') } } : undefined,
    );
    return res.data ?? {};
  },

  /** Who escalated is taken from the token, so it is not sent. */
  async escalate(key: string, input: EscalateInput): Promise<ConversationState> {
    const res = await api.post<ConversationState>(conversationPath(key, '/escalate'), input);
    return res.data;
  },

  /** The person taking over is the caller, so only the level is sent. */
  /** Back to the agent: off every human level, nobody assigned. */
  async handBack(key: string): Promise<ConversationState> {
    const res = await api.post<ConversationState>(conversationPath(key, '/hand-back'));
    return res.data;
  },

  async takeOver(key: string, levelId: string): Promise<ConversationState> {
    const res = await api.post<ConversationState>(conversationPath(key, '/take-over'), { levelId });
    return res.data;
  },

  async resolve(key: string): Promise<ConversationState> {
    const res = await api.post<ConversationState>(conversationPath(key, '/resolve'), {});
    return res.data;
  },

  /** The server knows which rung it is on, so it decides where a reopen lands. */
  async reopen(key: string): Promise<ConversationState> {
    const res = await api.post<ConversationState>(conversationPath(key, '/reopen'));
    return res.data;
  },

  async snooze(key: string, until: string): Promise<ConversationState> {
    const res = await api.post<ConversationState>(conversationPath(key, '/snooze'), { until });
    return res.data;
  },

  /**
   * One route (IA-17): the server checks the member, writes the state and
   * every message of the conversation, and tells the assignee.
   */
  async assign(_eventId: string, key: string, userId: string | null): Promise<void> {
    await api.post(conversationPath(key, '/assign'), { userId });
  },

  /**
   * A draft from the agent: the reply, how sure it is, the intent it read,
   * the customer's language and the knowledge it drew on. `language` makes
   * the agent answer in that language instead of the customer's.
   *
   * A refusal (no AI on the plan, the allowance used, the free Starter plan, a
   * blocked customer, a closed reply window) is thrown with the API's code,
   * so the draft box can say why. TODO(api): streaming (SSE).
   */
  async getDraft(eventId: string, key: string, language: string | null): Promise<AiDraft> {
    try {
      // A POST: each new draft spends an AI reply, so it is never retried
      // by the client on its own. The API keeps the draft for the same
      // message and language, so asking again costs nothing.
      const res = await api.post<AiDraft>(`/inbox/${eventId}/ai-reply`, language ? { language } : {});
      if (!res.data?.text?.trim()) throw new EmptyDraftError();
      return { ...res.data, isSample: false };
    } catch (err) {
      if (SAMPLE_DRAFTS) return mockInboxAgent.sampleDraft(key, language);
      throw err;
    }
  },

  /**
   * "Somebody is typing here." Sent every few seconds while a person writes,
   * so the agent in Auto mode does not send over them. Best effort: a missed
   * heartbeat only means the lock lifts a few seconds early.
   */
  async noteTyping(key: string): Promise<void> {
    await api.post(conversationPath(key, '/typing')).catch(() => undefined);
  },

  /**
   * REAL: POST /ai/translate. Used by the draft box's language switch, since
   * the AI reply endpoint has no language override yet.
   */
  async translateDraft(text: string, targetLanguage: string): Promise<string> {
    const res = await api.post<{ translatedText?: string }>('/ai/translate', { text, targetLanguage });
    return res.data?.translatedText?.trim() || text;
  },

  /**
   * REAL: POST /ai/translate, the same endpoint as the draft switch. Used for
   * the caption under a customer message written in another language (A02).
   * Requested only from an open thread, never for list rows.
   */
  translateMessage(text: string, targetLanguage: string): Promise<string> {
    return inboxAgentService.translateDraft(text, targetLanguage);
  },

  /**
   * Corrects the intent the agent picked. The entry it got wrong is named, so
   * the correction records what was chosen as well as what it should be.
   */
  async sendIntentFeedback(key: string, entryId: string, intent: string): Promise<AiActivityEntry> {
    const res = await api.post<AiActivityEntry>(
      conversationPath(key, `/activity/${encodeURIComponent(entryId)}/feedback`),
      { intent },
    );
    return res.data;
  },

  async noteDraftEdit(key: string, changedChars: number): Promise<AiActivityEntry> {
    const res = await api.post<AiActivityEntry>(conversationPath(key, '/draft-edits'), {
      changedChars,
    });
    return res.data;
  },

  async getCustomers(): Promise<Record<string, CustomerRecord>> {
    const res = await api.get<Record<string, CustomerRecord>>('/inbox/agent/customers');
    return res.data ?? {};
  },

  async saveCustomerNotes(customerKey: string, notes: string): Promise<CustomerRecord> {
    const res = await api.put<CustomerRecord>(
      `/inbox/agent/customers/${encodeURIComponent(customerKey)}/notes`,
      { notes },
    );
    return res.data;
  },

  /** Blocking also stops the agent replying, which is why the server decides it. */
  async setCustomerBlocked(customerKey: string, blocked: boolean): Promise<CustomerRecord> {
    const res = await api.post<CustomerRecord>(
      `/inbox/agent/customers/${encodeURIComponent(customerKey)}/${blocked ? 'block' : 'unblock'}`,
    );
    return res.data;
  },

  /** Marks a reply as machine-written, so everyone reading the thread sees it. */
  async markAiReply(key: string, replyId: string): Promise<ConversationState> {
    const res = await api.post<ConversationState>(conversationPath(key, '/ai-replies'), { replyId });
    return res.data;
  },

  /** Allowed for ten seconds after an automatic send; the server enforces that. */
  async undoAutoSend(key: string): Promise<ConversationState> {
    const res = await api.post<ConversationState>(conversationPath(key, '/undo-auto-send'));
    return res.data;
  },

  async getActivity(key: string): Promise<AiActivityEntry[]> {
    const res = await api.get<AiActivityEntry[]>(conversationPath(key, '/activity'));
    return res.data ?? [];
  },

  async listSources(): Promise<KnowledgeSourceView[]> {
    const res = await api.get<KnowledgeSourceView[]>('/inbox/agent/knowledge');
    return res.data ?? [];
  },

  /**
   * Adds a source. A website is read there and then, so the source comes back
   * either ready or failed with the reason.
   */
  async addSource(
    input: Pick<KnowledgeSource, 'kind' | 'title' | 'language' | 'content'>,
  ): Promise<KnowledgeSourceView> {
    const res = await api.post<KnowledgeSourceView>('/inbox/agent/knowledge', input);
    return res.data;
  },

  /** Edits a source; its content is read again when it changed (IA-36). */
  async updateSource(sourceId: string, patch: Partial<Pick<KnowledgeSource, 'title' | 'language' | 'content'>>): Promise<KnowledgeSourceView> {
    const res = await api.patch<KnowledgeSourceView>(`/inbox/agent/knowledge/${encodeURIComponent(sourceId)}`, patch);
    return res.data;
  },

  /** Reads a website source again (IA-36). */
  async refreshSource(sourceId: string): Promise<KnowledgeSourceView> {
    const res = await api.post<KnowledgeSourceView>(`/inbox/agent/knowledge/${encodeURIComponent(sourceId)}/refresh`);
    return res.data;
  },

  async removeSource(sourceId: string): Promise<void> {
    await api.delete(`/inbox/agent/knowledge/${encodeURIComponent(sourceId)}`);
  },
};
